/**
 * Authentication Store (Zustand)
 *
 * Global state management for authentication:
 * - User authentication status
 * - User information
 * - Login/logout actions
 * - Loading and error states
 */

import { create } from 'zustand';
import type { User, LoginRequest } from '@/types/auth';
import { login as apiLogin, adminLogin as apiAdminLogin, logout as apiLogout, getCurrentUser } from '@/api/auth';
import { isAuthenticated } from '@/utils/token';
import { queryClient } from '@/main';

interface AuthStore {
  // State
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  adminLogin: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

/**
 * Authentication store with Zustand
 */
export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  isAuthenticated: false,
  user: null,
  isLoading: true, // CRITICAL FIX: Start as true to prevent premature redirects during auth initialization
  error: null,

  /**
   * Login action (V107)
   * For regular users: ORG_ADMIN, ORG_POWER, ORG_USER
   */
  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiLogin(credentials);

      const user: User = {
        username: response.username,
        email: response.email,
        name: response.name,
        roles: response.roles,
        organization: response.organization, // Include organization from API response
      };

      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.message ||
        '로그인에 실패했습니다.';

      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: errorMessage,
      });

      throw error;
    }
  },

  /**
   * Admin login action (V108)
   * For system administrators: SUPER_ADMIN, ADMIN
   */
  adminLogin: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiAdminLogin(credentials);

      const user: User = {
        username: response.username,
        email: response.email,
        name: response.name,
        roles: response.roles,
        organization: response.organization, // Include organization from API response
      };

      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.message ||
        '관리자 로그인에 실패했습니다.';

      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: errorMessage,
      });

      throw error;
    }
  },

  /**
   * Logout action
   * Clears React Query cache to prevent stale data from previous user
   */
  logout: async () => {
    set({ isLoading: true });

    try {
      await apiLogout();
    } catch (error) {
      
    } finally {
      // Clear all React Query cache to prevent data leakage between users
      queryClient.clear();

      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });
    }
  },

  /**
   * Initialize authentication state from localStorage
   * Call this on app startup
   */
  initialize: () => {
    const authenticated = isAuthenticated();

    if (authenticated) {
      const user = getCurrentUser();

      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
      });
    } else {
      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));
